import { useMemo, useState } from 'react'
import {
  setListingVariants,
  type ListingOption,
  type ListingVariantPayload,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'

interface Props {
  listingId: string
  defaultPriceCents?: number
  onSaved?: (variantCount: number) => void
}

/**
 * Minimal variant editor for managed-shop listings.
 *
 * Flow:
 *   1. Admin names up to 2 option dimensions (e.g., Color, Size) and enters
 *      comma-separated values for each.
 *   2. "Generate combinations" expands the cartesian product into a variant
 *      table, seeded with `defaultPriceCents` and qty=0.
 *   3. Admin edits per-row price / qty / SKU / weight, then saves.
 */
export function VariantsEditor({
  listingId,
  defaultPriceCents = 0,
  onSaved,
}: Props) {
  const [opt1Name, setOpt1Name] = useState('Color')
  const [opt1Values, setOpt1Values] = useState('')
  const [opt2Name, setOpt2Name] = useState('')
  const [opt2Values, setOpt2Values] = useState('')
  const [variants, setVariants] = useState<ListingVariantPayload[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedCount, setSavedCount] = useState<number | null>(null)

  const parsedOptions = useMemo<ListingOption[]>(() => {
    const out: ListingOption[] = []
    const splitValues = (raw: string) =>
      Array.from(
        new Set(
          raw
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      )
    if (opt1Name.trim() && opt1Values.trim()) {
      out.push({ name: opt1Name.trim(), values: splitValues(opt1Values) })
    }
    if (opt2Name.trim() && opt2Values.trim()) {
      out.push({ name: opt2Name.trim(), values: splitValues(opt2Values) })
    }
    return out.filter((o) => o.values.length > 0)
  }, [opt1Name, opt1Values, opt2Name, opt2Values])

  const canGenerate = parsedOptions.length >= 1

  const generate = () => {
    setError(null)
    if (!canGenerate) return
    const [a, b] = parsedOptions
    const rows: ListingVariantPayload[] = []
    const aVals = a.values
    const bVals = b ? b.values : [undefined as unknown as string]
    for (const av of aVals) {
      for (const bv of bVals) {
        const ov: Record<string, string> = { [a.name]: av }
        if (b && bv !== undefined) ov[b.name] = bv
        rows.push({
          option_values: ov,
          price_cents: defaultPriceCents,
          qty_available: 0,
          sku: null,
          weight_oz: null,
        })
      }
    }
    setVariants(rows)
  }

  const updateRow = (idx: number, patch: Partial<ListingVariantPayload>) => {
    setVariants((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, ...patch } : row)),
    )
  }

  const save = async () => {
    setError(null)
    if (parsedOptions.length === 0 || variants.length === 0) {
      setError('Add at least one option dimension and generate combinations.')
      return
    }
    setSaving(true)
    const res = await setListingVariants(listingId, {
      options: parsedOptions,
      variants,
    })
    setSaving(false)
    if (!res.ok) {
      setError(res.error?.message || 'Failed to save variants')
      return
    }
    const count = res.data?.variant_count ?? variants.length
    setSavedCount(count)
    onSaved?.(count)
  }

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 dark:border-[#3d2c4f] p-4">
      <div>
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
          Variants
        </h4>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Up to 2 option dimensions (e.g. Color, Size). Enter values comma-separated,
          click generate, then edit each combination's price and stock.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <CustomTextInput
          label="Option 1 name"
          value={opt1Name}
          onChange={(e) => setOpt1Name(e.target.value)}
          placeholder="Color"
        />
        <CustomTextInput
          label="Option 1 values (comma-separated)"
          value={opt1Values}
          onChange={(e) => setOpt1Values(e.target.value)}
          placeholder="Red, Blue, Green"
        />
        <CustomTextInput
          label="Option 2 name (optional)"
          value={opt2Name}
          onChange={(e) => setOpt2Name(e.target.value)}
          placeholder="Size"
        />
        <CustomTextInput
          label="Option 2 values"
          value={opt2Values}
          onChange={(e) => setOpt2Values(e.target.value)}
          placeholder="S, M, L"
        />
      </div>

      <div className="flex gap-2">
        <CustomButton
          onClick={generate}
          disabled={!canGenerate}
          variant="secondary"
        >
          Generate combinations ({parsedOptions.reduce(
            (acc, o) => acc * o.values.length,
            1,
          )})
        </CustomButton>
      </div>

      {variants.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-[#3d2c4f]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-[#2a1f35]">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Combination</th>
                <th className="px-3 py-2 text-left font-medium">
                  Price (cents)
                </th>
                <th className="px-3 py-2 text-left font-medium">Qty</th>
                <th className="px-3 py-2 text-left font-medium">SKU</th>
                <th className="px-3 py-2 text-left font-medium">Weight (oz)</th>
              </tr>
            </thead>
            <tbody>
              {variants.map((v, idx) => (
                <tr
                  key={idx}
                  className="border-t border-gray-100 dark:border-[#3d2c4f]"
                >
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                    {Object.entries(v.option_values)
                      .map(([k, val]) => `${k}: ${val}`)
                      .join(' / ')}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      className="w-24 rounded border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-2 py-1"
                      value={v.price_cents}
                      onChange={(e) =>
                        updateRow(idx, {
                          price_cents: Number(e.target.value) || 0,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      className="w-20 rounded border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-2 py-1"
                      value={v.qty_available}
                      onChange={(e) =>
                        updateRow(idx, {
                          qty_available: Number(e.target.value) || 0,
                        })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="text"
                      className="w-28 rounded border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-2 py-1"
                      value={v.sku ?? ''}
                      onChange={(e) =>
                        updateRow(idx, { sku: e.target.value || null })
                      }
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={1}
                      max={160}
                      className="w-20 rounded border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-2 py-1"
                      value={v.weight_oz ?? ''}
                      onChange={(e) =>
                        updateRow(idx, {
                          weight_oz: e.target.value
                            ? Number(e.target.value)
                            : null,
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {savedCount !== null && !error && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/30 p-3 text-sm text-green-700 dark:text-green-300">
          Saved {savedCount} variant{savedCount === 1 ? '' : 's'}.
        </div>
      )}

      <div>
        <CustomButton
          onClick={save}
          disabled={saving || variants.length === 0}
          variant="primary"
        >
          {saving ? 'Saving…' : 'Save variants'}
        </CustomButton>
      </div>
    </div>
  )
}
